import React, { useState, useCallback, useLayoutEffect } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Animated,
  StatusBar,
  Image,
  Linking,
} from 'react-native';
import {
  Text,
  useTheme,
  Surface,
  Chip,
  Button,
  Divider,
} from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import {
  Star,
  Check,
  Award,
  Coffee,
  MessageCircle,
  GitCompare,
  ExternalLink,
  ChevronRight,
} from 'lucide-react-native';
import { useEquipmentItem } from '@coffeeplexity/api-client';
import { useWishlistItem as useFavoriteItem } from '@coffeeplexity/shared';
import { HeaderActions, UserAvatar } from '../components/ui';
import { logger } from '../utils/logger';

function normalizeAffiliateLinks(
  links: any
): Array<{ retailer: string; url: string }> {
  if (!links) return [];
  if (Array.isArray(links)) {
    return links
      .map((item: any) => ({
        retailer: item?.retailer || 'Retailer',
        url: String(item?.url || ''),
      }))
      .filter(item => !!item.url);
  }
  // object map case
  return Object.entries(links)
    .map(([retailer, url]) => ({ retailer, url: String(url) }))
    .filter(item => !!item.url);
}

function buildFallbackAffiliateLinks(
  equipment: any
): Array<{ retailer: string; url: string }> {
  const query = encodeURIComponent(equipment?.name || 'coffee equipment');
  return [
    { retailer: 'Amazon', url: `https://www.amazon.com/s?k=${query}` },
    {
      retailer: 'Google Shopping',
      url: `https://www.google.com/search?tbm=shop&q=${query}`,
    },
    { retailer: 'eBay', url: `https://www.ebay.com/sch/i.html?_nkw=${query}` },
  ];
}

type EquipmentDetailRouteProp = RouteProp<
  {
    EquipmentDetail: { equipmentId: string };
  },
  'EquipmentDetail'
>;

export function EquipmentDetailScreen() {
  const theme = useTheme();
  const navigation = useNavigation<any>();
  const route = useRoute<EquipmentDetailRouteProp>();
  const { equipmentId } = route.params;

  const [activeTab, setActiveTab] = useState('overview');
  const [scrollY] = useState(new Animated.Value(0));

  const { data: equipment, isLoading, error } = useEquipmentItem(equipmentId);
  const { isInWishlist: isFavorited, toggle: toggleFavorite } =
    useFavoriteItem(equipmentId);

  const handleFavoriteToggle = useCallback(() => {
    toggleFavorite(equipment);
  }, [toggleFavorite, equipment]);

  // Set up header actions for share and like
  useLayoutEffect(() => {
    if (equipment) {
      (navigation as any).setOptions({
        headerRight: () => (
          <HeaderActions
            isLiked={isFavorited}
            onLikePress={handleFavoriteToggle}
            shareContent={{
              title: equipment.name,
              message: `Check out this amazing coffee equipment: ${equipment.name}\n\n${equipment.description}`,
              url: `https://coffeeplexity.com/equipment/${equipment.id}`,
            }}
            testID='equipment-header-actions'
          />
        ),
      });
    }
  }, [navigation, equipment, isFavorited, handleFavoriteToggle]);

  const handleBuyNow = useCallback((retailer: string, url: string) => {
    try {
      if (url) {
        Linking.openURL(url);
      }
    } catch (e) {
      logger.log('Failed to open url', retailer, url, e);
    }
  }, []);

  const renderImage = () => {
    if (!equipment) return null;

    const imageUrl =
      equipment.images?.length > 0
        ? equipment.images[0]
        : 'https://images.unsplash.com/photo-1559056199-641a0ac8b55e?w=800&h=600&fit=crop';

    return (
      <View style={styles.imageSection}>
        <Image
          source={{
            uri: imageUrl,
          }}
          style={styles.equipmentImage}
          resizeMode='contain'
        />
      </View>
    );
  };

  const renderProductInfo = () => {
    if (!equipment) return null;

    const priceDisplay =
      equipment.price_min && equipment.price_max
        ? equipment.price_min !== equipment.price_max
          ? `$${equipment.price_min} - $${equipment.price_max}`
          : `$${equipment.price_min}`
        : equipment.price_min
          ? `$${equipment.price_min}`
          : 'Contact for price';

    return (
      <Surface
        style={[styles.productInfo, { backgroundColor: theme.colors.surface }]}
        elevation={1}
      >
        <View style={styles.productHeader}>
          <View style={styles.brandContainer}>
            <Text
              variant='labelMedium'
              style={[styles.brandText, { color: theme.colors.primary }]}
            >
              {equipment.brand?.toUpperCase()}
            </Text>
            <View style={styles.badges}>
              {equipment.is_featured && (
                <Chip
                  icon={({ size }) => <Award size={size} color='#d97706' />}
                  style={[styles.featuredBadge, { backgroundColor: '#fef3c7' }]}
                  textStyle={[styles.badgeText, { color: '#d97706' }]}
                >
                  Featured
                </Chip>
              )}
            </View>
          </View>

          <Text
            variant='headlineSmall'
            style={[styles.productTitle, { color: theme.colors.onSurface }]}
          >
            {equipment.name}
          </Text>

          <View style={styles.metaInfo}>
            <View style={styles.ratingContainer}>
              <Star
                size={16}
                color={equipment.rating ? '#f59e0b' : '#d4d4d4'}
                fill={equipment.rating ? '#f59e0b' : '#d4d4d4'}
              />
              <Text
                variant='labelMedium'
                style={[
                  styles.ratingText,
                  { opacity: equipment.rating ? 1 : 0.5 },
                ]}
              >
                {equipment.rating || '0.0'}
              </Text>
              <Text
                variant='labelSmall'
                style={[
                  styles.reviewCount,
                  { opacity: equipment.rating ? 0.7 : 0.5 },
                ]}
              >
                ({equipment.review_count || 0} reviews)
              </Text>
            </View>
            <Text variant='labelMedium' style={styles.categoryText}>
              {equipment.type?.replace(/_/g, ' ')}
            </Text>
          </View>
        </View>

        <Divider style={styles.divider} />

        <View style={styles.priceContainer}>
          <View style={styles.priceRow}>
            <Text
              variant='headlineMedium'
              style={[styles.price, { color: '#059669' }]}
            >
              {priceDisplay}
            </Text>
          </View>
        </View>

        <Divider style={styles.divider} />

        <View style={styles.descriptionContainer}>
          <Text
            variant='bodyMedium'
            style={[
              styles.description,
              { color: theme.colors.onSurfaceVariant },
            ]}
          >
            {equipment.description}
          </Text>
        </View>
      </Surface>
    );
  };

  const renderCreatorInfo = () => {
    if (!equipment) return null;

    // Check if equipment has creator information
    const hasCreator = equipment.creator_id && equipment.creator_name;

    // Determine creator display info
    const creatorName = hasCreator
      ? equipment.creator_name
      : 'Coffeeplexity Team';

    const creatorRole = hasCreator
      ? equipment.creator_role || 'Equipment Contributor'
      : 'Verified Equipment Curator';

    const isVerified = hasCreator ? equipment.creator_is_verified : true;

    const avatarUrl = hasCreator ? equipment.creator_avatar_url : null;

    // Determine navigation based on role
    const isActualCreator = hasCreator && equipment.creator_role === 'Creator';
    const canNavigate = hasCreator; // All users with IDs can be navigated to

    return (
      <Surface
        style={[
          styles.creatorSection,
          { backgroundColor: theme.colors.surface },
        ]}
        elevation={1}
      >
        <TouchableOpacity
          style={styles.creatorHeader}
          onPress={() => {
            if (canNavigate && equipment.creator_id) {
              if (isActualCreator) {
                // Navigate to creator profile for actual creators
                (navigation as any).navigate('home', {
                  screen: 'creators',
                  params: {
                    screen: 'creatorProfile',
                    params: {
                      creatorId: equipment.creator_id,
                    },
                  },
                });
              } else {
                // Navigate to user profile for regular users
                (navigation as any).navigate('userProfile', {
                  userId: equipment.creator_id,
                });
              }
            }
          }}
          disabled={!canNavigate}
          activeOpacity={canNavigate ? 0.7 : 1}
        >
          <UserAvatar
            avatarUrl={avatarUrl}
            name={creatorName}
            size={48}
            testID='equipment-creator-avatar'
          />
          <View style={styles.creatorDetails}>
            <Text variant='labelSmall' style={styles.creatorLabel}>
              Added by
            </Text>
            <View style={styles.creatorNameRow}>
              <Text variant='titleSmall' style={styles.creatorName}>
                {creatorName}
              </Text>
              {isVerified && (
                <Check size={14} color='#059669' strokeWidth={3} />
              )}
            </View>
            <Text variant='bodySmall' style={styles.creatorBadge}>
              {creatorRole}
            </Text>
          </View>
          {canNavigate && (
            <View style={styles.creatorArrow}>
              <ChevronRight size={20} color={theme.colors.onSurfaceVariant} />
            </View>
          )}
        </TouchableOpacity>
      </Surface>
    );
  };

  const renderActionButtons = () => {
    if (!equipment) return null;

    const aff = normalizeAffiliateLinks(equipment.affiliate_links);
    const links = aff.length > 0 ? aff : buildFallbackAffiliateLinks(equipment);

    if (links.length === 0) return null;

    return (
      <Surface
        style={[
          styles.actionButtons,
          { backgroundColor: theme.colors.surface },
        ]}
        elevation={1}
      >
        <View style={styles.retailerLinks}>
          <Text variant='titleMedium' style={styles.buyFromTitle}>
            Where to Buy
          </Text>
          <Text variant='bodySmall' style={styles.commissionText}>
            We earn a small commission when you purchase through these links
          </Text>
          <View style={styles.retailerGrid}>
            {links.slice(0, 3).map(({ retailer, url }) => (
              <TouchableOpacity
                key={retailer}
                onPress={() => handleBuyNow(retailer, url)}
                style={styles.retailerButton}
                activeOpacity={0.6}
              >
                <Text variant='bodyMedium' style={styles.retailerText}>
                  {retailer}
                </Text>
                <ExternalLink size={14} color={'#007AFF'} />
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </Surface>
    );
  };

  const renderTabContent = () => {
    if (!equipment) return null;

    switch (activeTab) {
      case 'overview':
        return (
          <View style={styles.tabContent}>
            <Text variant='titleMedium' style={styles.sectionTitle}>
              About this Equipment
            </Text>
            <Text variant='bodyMedium' style={styles.sectionText}>
              {equipment.description}
            </Text>

            {equipment.specifications &&
              Object.keys(equipment.specifications).length > 0 && (
                <>
                  <Text
                    variant='titleMedium'
                    style={[styles.sectionTitle, styles.sectionTitleSpaced]}
                  >
                    Key Features
                  </Text>
                  <View style={styles.featuresGrid}>
                    {Object.entries(equipment.specifications)
                      .slice(0, 6)
                      .map(([key, value]) => (
                        <View key={key} style={styles.featureItem}>
                          <View style={styles.featureBullet} />
                          <Text variant='bodySmall' style={styles.featureText}>
                            <Text style={styles.featureKey}>
                              {key.replace(/_/g, ' ')}:
                            </Text>{' '}
                            <Text style={styles.featureValue}>
                              {String(value)}
                            </Text>
                          </Text>
                        </View>
                      ))}
                  </View>
                </>
              )}
          </View>
        );

      case 'specs':
        return (
          <View style={styles.tabContent}>
            <Text variant='titleMedium' style={styles.sectionTitle}>
              Technical Specifications
            </Text>
            {equipment.specifications &&
            Object.keys(equipment.specifications).length > 0 ? (
              <View style={styles.specsContainer}>
                {Object.entries(equipment.specifications).map(
                  ([key, value]) => (
                    <View key={key} style={styles.specRow}>
                      <Text variant='labelMedium' style={styles.specKey}>
                        {key.replace(/_/g, ' ')}
                      </Text>
                      <Text variant='bodyMedium' style={styles.specValue}>
                        {String(value)}
                      </Text>
                    </View>
                  )
                )}
              </View>
            ) : (
              <Text variant='bodyMedium' style={styles.emptyText}>
                No detailed specifications available.
              </Text>
            )}
          </View>
        );

      case 'reviews':
        return (
          <View style={styles.tabContent}>
            <View style={styles.emptyStateContainer}>
              <MessageCircle size={48} color={theme.colors.onSurfaceVariant} />
              <Text variant='titleMedium' style={styles.emptyStateTitle}>
                Reviews coming soon!
              </Text>
              <Text variant='bodyMedium' style={styles.emptyStateText}>
                We're building a comprehensive review system for equipment.
              </Text>
            </View>
          </View>
        );

      case 'comparison':
        return (
          <View style={styles.tabContent}>
            <View style={styles.emptyStateContainer}>
              <GitCompare size={48} color={theme.colors.onSurfaceVariant} />
              <Text variant='titleMedium' style={styles.emptyStateTitle}>
                Comparison coming soon!
              </Text>
              <Text variant='bodyMedium' style={styles.emptyStateText}>
                Compare specifications, prices, and reviews side by side.
              </Text>
            </View>
          </View>
        );

      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <SafeAreaView
        style={[styles.container, { backgroundColor: theme.colors.background }]}
      >
        <View style={styles.loadingContainer}>
          <Coffee size={56} color={theme.colors.onSurfaceVariant} />
          <Text variant='titleLarge' style={styles.loadingTitle}>
            Loading equipment...
          </Text>
          <Text variant='bodyMedium' style={styles.loadingSubtitle}>
            Finding the perfect coffee gear
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  if (error || !equipment) {
    return (
      <SafeAreaView
        style={[styles.container, { backgroundColor: theme.colors.background }]}
      >
        <View style={styles.errorContainer}>
          <Coffee size={64} color={theme.colors.onSurfaceVariant} />
          <Text variant='headlineSmall' style={styles.errorTitle}>
            Equipment Not Found
          </Text>
          <Text variant='bodyMedium' style={styles.errorText}>
            The equipment you're looking for doesn't exist or has been removed.
          </Text>
          <Button
            mode='contained'
            onPress={() => navigation.goBack()}
            style={styles.backButton}
          >
            Back to Equipment
          </Button>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
    >
      <StatusBar barStyle={theme.dark ? 'light-content' : 'dark-content'} />

      <Animated.ScrollView
        style={styles.scrollView}
        onScroll={Animated.event(
          [{ nativeEvent: { contentOffset: { y: scrollY } } }],
          { useNativeDriver: false }
        )}
        scrollEventThrottle={16}
        showsVerticalScrollIndicator={false}
      >
        {renderImage()}
        {renderProductInfo()}
        {renderCreatorInfo()}
        {renderActionButtons()}

        {/* Tabs Section */}
        <Surface
          style={[
            styles.tabsContainer,
            { backgroundColor: theme.colors.surface },
          ]}
          elevation={1}
        >
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={styles.tabScrollView}
            contentContainerStyle={styles.tabScrollContent}
          >
            <View style={styles.customTabsContainer}>
              {[
                { value: 'overview', label: 'Overview', icon: 'information' },
                { value: 'specs', label: 'Specifications', icon: 'cog' },
                { value: 'reviews', label: 'Reviews', icon: 'message' },
                { value: 'comparison', label: 'Compare', icon: 'compare' },
              ].map(tab => (
                <TouchableOpacity
                  key={tab.value}
                  style={[
                    styles.customTab,
                    activeTab === tab.value && styles.customTabActive,
                  ]}
                  onPress={() => setActiveTab(tab.value)}
                >
                  <Text
                    style={[
                      styles.customTabText,
                      activeTab === tab.value && styles.customTabTextActive,
                    ]}
                  >
                    {tab.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </ScrollView>
          {renderTabContent()}
        </Surface>

        <View style={styles.bottomPadding} />
      </Animated.ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  buyNowButton: {
    backgroundColor: '#e8def8',
    borderRadius: 20,
  },
  scrollView: {
    flex: 1,
  },
  imageSection: {
    height: 300,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 16,
  },
  equipmentImage: {
    width: '100%',
    height: '100%',
    borderRadius: 16,
  },
  productInfo: {
    margin: 16,
    borderRadius: 16,
    padding: 20,
  },
  productHeader: {
    marginBottom: 16,
  },
  brandContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  brandText: {
    fontWeight: '600',
    letterSpacing: 0.5,
  },
  badges: {
    flexDirection: 'row',
    gap: 8,
  },
  verifiedBadge: {
    backgroundColor: '#dcfce7',
  },
  featuredBadge: {
    backgroundColor: '#fef3c7',
  },
  badgeText: {
    fontSize: 10,
    fontWeight: '600',
  },
  productTitle: {
    fontWeight: '700',
    marginBottom: 12,
    lineHeight: 28,
  },
  metaInfo: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  ratingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  ratingText: {
    fontWeight: '600',
  },
  reviewCount: {
    opacity: 0.7,
  },
  categoryText: {
    textTransform: 'capitalize',
    opacity: 0.7,
  },
  divider: {
    marginVertical: 16,
  },
  priceContainer: {
    marginBottom: 16,
  },
  priceRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  price: {
    fontWeight: '700',
  },
  valueIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
  },
  valueText: {
    fontSize: 12,
    letterSpacing: 0.2,
  },
  descriptionContainer: {
    marginBottom: 16,
  },
  description: {
    lineHeight: 22,
  },
  tagsContainer: {
    marginBottom: 16,
  },
  tagsTitle: {
    marginBottom: 8,
    fontWeight: '600',
  },
  tagsWrapper: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  tag: {
    borderRadius: 8,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  tagText: {
    fontSize: 12,
  },
  creatorSection: {
    margin: 16,
    marginTop: 0,
    borderRadius: 16,
    padding: 16,
  },
  creatorHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  creatorDetails: {
    flex: 1,
  },
  creatorLabel: {
    opacity: 0.6,
    marginBottom: 2,
  },
  creatorNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 2,
  },
  creatorName: {
    fontWeight: '600',
  },
  creatorBadge: {
    color: '#059669',
    fontSize: 12,
  },
  creatorArrow: {
    opacity: 0.5,
  },
  actionButtons: {
    margin: 16,
    marginTop: 0,
    borderRadius: 16,
    padding: 20,
  },
  retailerLinks: {
    marginTop: 0,
  },
  buyFromTitle: {
    fontWeight: '700',
    marginBottom: 8,
    color: '#1f2937',
  },
  commissionText: {
    marginBottom: 16,
    color: '#6b7280',
    textAlign: 'center',
    fontStyle: 'italic',
  },
  retailerGrid: {
    flexDirection: 'column',
  },
  retailerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 16,
    paddingHorizontal: 4,
  },
  retailerText: {
    flex: 1,
    color: '#007AFF',
    fontSize: 17,
    fontWeight: '400',
  },
  tabsContainer: {
    margin: 16,
    marginTop: 0,
    borderRadius: 16,
    overflow: 'hidden',
  },
  tabScrollView: {
    flexGrow: 0,
    paddingHorizontal: 8,
  },
  tabScrollContent: {
    paddingHorizontal: 16,
    paddingVertical: 16,
    paddingBottom: 0,
    alignItems: 'center',
  },
  customTabsContainer: {
    flexDirection: 'row',
    backgroundColor: '#f3f4f6',
    borderRadius: 20,
    padding: 4,
    minWidth: 500,
  },
  customTab: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 16,
    marginHorizontal: 2,
    backgroundColor: 'transparent',
  },
  customTabActive: {
    backgroundColor: '#ffffff',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  customTabText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#6b7280',
    textAlign: 'center',
  },
  customTabTextActive: {
    color: '#000000',
    fontWeight: '600',
  },
  tabContent: {
    padding: 20,
  },
  sectionTitle: {
    fontWeight: '600',
    marginBottom: 12,
  },
  sectionTitleSpaced: {
    marginTop: 20,
  },
  sectionText: {
    lineHeight: 22,
    opacity: 0.8,
  },
  featuresGrid: {
    gap: 8,
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
  },
  featureBullet: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#3b82f6',
    marginTop: 6,
  },
  featureText: {
    flex: 1,
    lineHeight: 20,
  },
  featureKey: {
    fontWeight: '600',
    textTransform: 'capitalize',
  },
  featureValue: {
    opacity: 0.8,
  },
  specsContainer: {
    gap: 12,
  },
  specRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  specKey: {
    fontWeight: '600',
    textTransform: 'capitalize',
    flex: 1,
  },
  specValue: {
    opacity: 0.8,
    textAlign: 'right',
  },
  emptyStateContainer: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  emptyStateTitle: {
    marginTop: 16,
    marginBottom: 8,
    fontWeight: '600',
  },
  emptyStateText: {
    textAlign: 'center',
    opacity: 0.7,
    lineHeight: 20,
  },
  emptyText: {
    opacity: 0.7,
  },
  bottomPadding: {
    height: 40,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
  },
  loadingTitle: {
    marginTop: 24,
    fontWeight: '600',
    textAlign: 'center',
  },
  loadingSubtitle: {
    marginTop: 8,
    opacity: 0.7,
    textAlign: 'center',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
  },
  errorTitle: {
    marginTop: 24,
    marginBottom: 12,
    fontWeight: '600',
    textAlign: 'center',
  },
  errorText: {
    marginBottom: 32,
    opacity: 0.7,
    textAlign: 'center',
    lineHeight: 22,
  },
  backButton: {
    paddingHorizontal: 16,
  },
});
